import React, { useState, useRef, useEffect, useCallback } from 'react';
import CredentialManager from './CredentialManager';
import { API_BASE_URL } from './config';

const sidebarStyle = {
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative',
  borderRight: '1px solid var(--border-color)',
  boxSizing: 'border-box',
  userSelect: 'none',
};

const headerStyle = {
  padding: '15px',
  borderBottom: '1px solid var(--border-color)',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px'
};

const searchInputStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '4px',
  border: '1px solid var(--border-color)',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  fontSize: '13px',
  outline: 'none',
  boxSizing: 'border-box'
};

const scrollAreaStyle = {
  flex: 1,
  overflowY: 'auto',
  padding: '15px'
};

const categoryStyle = {
  marginBottom: '10px'
};

const categoryHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  padding: '10px 10px 4px',
  color: 'var(--muted)',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  userSelect: 'none',
  transition: 'color 120ms ease',
};

/* palette list layout — single column, icon + label rows */
const listStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  marginTop: '4px',
};

const nodeStyle = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 10px',
  borderRadius: '5px',
  cursor: 'grab',
  background: 'transparent',
  color: 'var(--text)',
  fontSize: '13px',
  outline: 'none',
  userSelect: 'none',
  transition: 'background 100ms ease',
};

const nodeHoverStyle = {
  background: 'var(--panel-2)',
};

const iconStyle = {
  fontSize: '15px',
  flexShrink: 0,
  width: '18px',
  textAlign: 'center',
  opacity: 0.85,
};

const labelStyle = {
  fontWeight: 400,
  lineHeight: '1.3',
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const credentialButtonStyle = {
  width: '100%',
  padding: '8px',
  background: 'var(--accent-color)',
  color: 'var(--text-inverse)',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontWeight: '600',
  fontSize: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  transition: 'background 0.2s'
};

const resizeHandleStyle = {
  position: 'absolute',
  top: 0,
  right: -2,
  width: '4px',
  height: '100%',
  cursor: 'col-resize',
  zIndex: 100,
  transition: 'background-color 0.2s'
};

// Removed hardcoded nodeCategories

export default function Sidebar({ workflowType = 'standard' }) {
  const [width, setWidth] = useState(280);
  const [isResizing, setIsResizing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [nodeCategories, setNodeCategories] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({
    'Triggers': true,
    'AI & Agents': true,
    'Communication': true,
    'Productivity': true,
    'Database': true,
    'Logic & Flow': true,
    'Utilities': true,
    'Custom': true
  });
  const [hoveredNode, setHoveredNode] = useState(null);
  
  const sidebarRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
        console.warn("No token found, skipping node fetch");
        return;
    }
    fetch(`${API_BASE_URL}/nodes`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => {
        if (res.status === 401) {
            console.error("Unauthorized: Token may be expired");
            localStorage.removeItem('token');
            window.location.reload();
            return null;
        }
        if (!res.ok) throw new Error('Failed to fetch nodes');
        return res.json();
      })
      .then(nodes => {
        if (!Array.isArray(nodes)) return;
        const categories = {};
        nodes.forEach(node => {
          if (!categories[node.category]) {
            categories[node.category] = [];
          }
          categories[node.category].push(node);
        });
        setNodeCategories(categories);
        
        // Ensure new categories are expanded by default
        setExpandedCategories(prev => {
          const next = { ...prev };
          Object.keys(categories).forEach(cat => {
             if (next[cat] === undefined) next[cat] = true;
          });
          return next;
        });
      })
      .catch(err => console.error("Failed to fetch nodes:", err));
  }, []);

  const startResizing = useCallback(() => {
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback(
    (mouseMoveEvent) => {
      if (isResizing && sidebarRef.current) {
        const newWidth = mouseMoveEvent.clientX - sidebarRef.current.getBoundingClientRect().left;
        if (newWidth > 200 && newWidth < 500) {
          setWidth(newWidth);
        }
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResizing);
    }
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  const onDragStart = (event, nodeType, label, icon) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('application/reactflow/label', label);
    if (icon) {
      event.dataTransfer.setData('application/reactflow/icon', icon);
    }
    event.dataTransfer.effectAllowed = 'move';
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // §4.79 Multi-Agent Orchestration Studio — when an agent_graph workflow is
  // active, restrict the palette to AI Agent nodes only.
  const isAgentGraphMode = workflowType === 'agent_graph';
  const AGENT_GRAPH_ALLOWED_TYPES = new Set(['ai_agent']);

  const filteredCategories = Object.entries(nodeCategories).reduce((acc, [category, nodes]) => {
    const filteredNodes = nodes.filter(node => {
      if (isAgentGraphMode && !AGENT_GRAPH_ALLOWED_TYPES.has(node.type)) return false;
      return (
        node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    if (filteredNodes.length > 0) {
      acc[category] = filteredNodes;
    }
    return acc;
  }, {});

  return (
    <aside ref={sidebarRef} style={{ ...sidebarStyle, width: `${width}px` }}>
      <div
        onMouseDown={startResizing}
        style={{
          ...resizeHandleStyle,
          backgroundColor: isResizing ? 'var(--accent-color)' : 'transparent',
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--accent-color)'}
        onMouseLeave={(e) => !isResizing && (e.currentTarget.style.backgroundColor = 'transparent')}
      />
      
      <div style={headerStyle}>
        <div style={{ fontWeight: 600, fontSize: '12px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Nodes
        </div>
        <input
          type="text"
          placeholder="Search…"
          style={searchInputStyle}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div style={scrollAreaStyle}>
        {/* §4.79 Multi-Agent Orchestration Studio — palette mode banner */}
        {isAgentGraphMode && (
          <div style={{
            margin: '0 8px 8px 8px',
            padding: '8px 10px',
            background: 'rgba(139,92,246,0.12)',
            border: '1px solid #8B5CF6',
            borderRadius: '6px',
            fontSize: '11px',
            color: 'var(--text-primary)',
            lineHeight: 1.4,
          }}>
            <strong>🤖 Agent Graph Mode</strong>
            <div style={{ marginTop: '4px', color: 'var(--text-secondary)', fontSize: '10px' }}>
              Only AI Agent nodes are available. Drag from an agent's right-side handle to delegate tasks.
            </div>
          </div>
        )}
        {/* Canvas Tools - always shown, not from API */}
        {!searchTerm && (
          <div style={categoryStyle}>
            <div
              style={categoryHeaderStyle}
              onClick={() => toggleCategory('Canvas Tools')}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-primary)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-tertiary)')}
            >
              <span style={{ marginRight: '6px', fontSize: '10px' }}>
                {expandedCategories['Canvas Tools'] !== false ? '▼' : '▶'}
              </span>
              Canvas Tools
              <span style={{ marginLeft: 'auto', background: 'var(--panel-2)', padding: '1px 6px', borderRadius: '999px', fontSize: '10px', color: 'var(--dim)' }}>
                1
              </span>
            </div>
            {expandedCategories['Canvas Tools'] !== false && (
              <div style={listStyle}>
                <div
                  className="sidebar-node-card"
                  style={{
                    ...nodeStyle,
                    ...(hoveredNode === 'sticky_note' ? nodeHoverStyle : {}),
                  }}
                  draggable
                  onDragStart={e => onDragStart(e, 'sticky_note', 'Sticky Note', '🗒')}
                  onMouseEnter={() => setHoveredNode('sticky_note')}
                  onMouseLeave={() => setHoveredNode(null)}
                  title="Add a text annotation to the canvas"
                >
                  <span style={iconStyle}>🗒</span>
                  <span style={labelStyle}>Sticky Note</span>
                </div>
              </div>
            )}
          </div>
        )}

        {Object.keys(filteredCategories).length === 0 && searchTerm && (
          <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '20px' }}>
            No nodes found
          </div>
        )}

        {Object.entries(filteredCategories).map(([category, nodes]) => (
          <div key={category} style={categoryStyle}>
            <div 
              style={categoryHeaderStyle} 
              onClick={() => toggleCategory(category)}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
            >
              <span style={{ marginRight: '6px', fontSize: '10px' }}>
                {expandedCategories[category] ? '▼' : '▶'}
              </span>
              {category}
              <span style={{ marginLeft: 'auto', background: 'var(--panel-2)', padding: '1px 6px', borderRadius: '999px', fontSize: '10px', color: 'var(--dim)' }}>
                {nodes.length}
              </span>
            </div>
            
            {expandedCategories[category] && (
              <div style={listStyle}>
                {nodes.map((node) => (
                  <div
                    key={node.type}
                    className="sidebar-node-card"
                    style={{
                      ...nodeStyle,
                      ...(hoveredNode === node.type ? nodeHoverStyle : {}),
                    }}
                    onDragStart={(event) => onDragStart(event, node.type, node.label, node.icon)}
                    draggable
                    onMouseEnter={() => setHoveredNode(node.type)}
                    onMouseLeave={() => setHoveredNode(null)}
                    title={node.description || node.label}
                  >
                    <span style={iconStyle}>{node.icon}</span>
                    <span style={labelStyle}>{node.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

    </aside>
  );
}
